import { Box, Typography, Button, Card, CardContent, Alert } from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  ContentCopy as ContentIcon,
  Upgrade as UpgradeIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface LatestWeeklyIdeaType {
  id: number;
  business_profile_id: number;
  dashboard_teaser: string;
  idea_summary: string;
}

interface QuickActionsProps {
  user: any;
  profiles: any[];
  usage: any;
  currentPlan: any;
  emailStatus: any;
  latestWeeklyIdea?: LatestWeeklyIdeaType | null;
  onNavigate: (path: string) => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({ 
  user, 
  profiles, 
  usage, 
  currentPlan,
  emailStatus,
  latestWeeklyIdea = null,
  onNavigate 
}) => {
  const { t } = useTranslation('dashboard');
  
  const hasProfiles = profiles && profiles.length > 0;
  const isFreePlan = !currentPlan || 
    currentPlan.name === 'Free' || 
    currentPlan.name === 'Free Plan' || 
    currentPlan.name === 'FreeTest';
  const usageCount = usage?.generations_used || (usage as any)?.content_generated || 0;
  const usageLimit = currentPlan?.monthly_limit || 1000;
  const usagePercentage = usageLimit > 0 ? (usageCount / usageLimit) * 100 : 0;
  const isNearLimit = usagePercentage >= 80;
  const isNewUser = usageCount === 0 && !hasProfiles;
  const isEmailVerified = emailStatus?.is_verified || user?.is_verified;

  // Determine if profile setup is incomplete
  const profileIncomplete = hasProfiles && profiles.some((profile: any) => 
    !profile.description || 
    !profile.preferred_platform_ids || 
    profile.preferred_platform_ids.length === 0
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Contextual Actions Card */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('quickActions.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('quickActions.subtitle')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Email Not Verified - Highest Priority */}
            {!isEmailVerified && (
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/profile')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.verifyEmail')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.verifyEmailDesc')}
                </Typography>
                <Typography variant="caption">
                  {t('quickActions.verifyEmailNote')}
                </Typography>
              </Alert>
            )}

            {/* New User - Get Started (only if email verified) */}
            {isEmailVerified && isNewUser && (
              <Alert 
                severity="info" 
                icon={<LightbulbIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/business-profiles')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.getStarted')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.getStartedDesc')}
                </Typography>
              </Alert>
            )}

            {/* No Profiles - Create Profile (only if email verified) */}
            {isEmailVerified && !hasProfiles && !isNewUser && (
              <Alert 
                severity="warning" 
                icon={<BusinessIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/business-profiles')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.createProfile')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.createProfileDesc')}
                </Typography>
              </Alert>
            )}

            {/* Profile Incomplete - Complete Setup (only if email verified) */}
            {isEmailVerified && profileIncomplete && (
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/business-profiles')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.completeSetup')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.profilesNeedInfo')}
                </Typography>
              </Alert>
            )}

            {/* Ready to Generate (only if email verified) */}
            {isEmailVerified && hasProfiles && !profileIncomplete && usageCount === 0 && (
              <Alert 
                severity="success" 
                icon={<ContentIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/generate')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.generateContent')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.readyToCreate')}
                </Typography>
              </Alert>
            )}

            {/* Usage Near Limit - Upgrade Prompt (only if email verified) */}
            {isEmailVerified && isNearLimit && isFreePlan && (
              <Alert 
                severity="warning" 
                icon={<UpgradeIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/billing')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.upgradePlan')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.usageWarning', { percentage: Math.round(usagePercentage) })}
                </Typography>
                <Typography variant="caption">
                  {t('quickActions.upgradeToGetMore')}
                </Typography>
              </Alert>
            )}

            {/* Weekly idea – Generate content with idea (only if user has a weekly idea) */}
            {isEmailVerified && latestWeeklyIdea != null && (() => {
              const idea = latestWeeklyIdea as LatestWeeklyIdeaType;
              return (
                <Alert
                  severity="info"
                  icon={<AutoAwesomeIcon />}
                sx={{
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  color: 'rgba(123, 31, 162, 0.9)',
                  '& .MuiAlert-icon': { color: '#7B1FA2' },
                  '& .MuiAlert-message': { color: 'text.primary' },
                }}
                  action={
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        onNavigate(
                          `/generate?profile=${idea.business_profile_id}&idea=${idea.id}`
                        )
                      }
                      sx={{ textTransform: 'none', bgcolor: 'primary.main' }}
                    >
                      {t('quickActions.generateWithIdea')}
                    </Button>
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t('quickActions.weeklyIdeaReady')}
                  </Typography>
                  {idea.dashboard_teaser ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {idea.dashboard_teaser}
                    </Typography>
                  ) : null}
                </Alert>
              );
            })()}

            {/* All Good - Continue Creating (only if email verified) */}
            {isEmailVerified && hasProfiles && !profileIncomplete && !isNearLimit && usageCount > 0 && (
              <Alert 
                severity="success" 
                icon={<CheckCircleIcon />}
                action={
                  <Button 
                    size="small" 
                    onClick={() => onNavigate('/generate')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('quickActions.createMore')}
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('quickActions.allGood')}
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

    </Box>
  );
};

export default QuickActions;
